import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { adminLogin } from '@/lib/api';
import { toast } from 'sonner';

const SECRET_CODE = '444';

export default function Footer() {
    const navigate = useNavigate();
    const [pinOpen, setPinOpen] = useState(false);
    const [pin, setPin] = useState('');

    const submitPin = async (e) => {
        e.preventDefault();
        if (pin !== SECRET_CODE) {
            toast.error('رمز غير صحيح');
            setPin('');
            return;
        }
        try {
            const token = await adminLogin('204791');
            localStorage.setItem('admin_token', token);
            setPinOpen(false);
            setPin('');
            navigate('/admin');
        } catch {
            toast.error('تعذّر الفتح');
        }
    };

    return (
        <footer
            className="relative bg-ink text-sand-100 pt-32 pb-12 overflow-hidden"
            data-testid="site-footer"
        >
            <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 pb-24 border-b border-sand-100/20">
                    <div className="lg:col-span-8">
                        <div className="font-display font-black text-[14vw] lg:text-[10vw] leading-[0.9] tracking-tight">
                            استطلاعات
                            <span className="text-emerald-signal">.</span>
                        </div>
                    </div>
                    <div className="lg:col-span-4 flex flex-col justify-end gap-8">
                        <p className="text-sand-100/70 leading-relaxed">
                            دليل عربي مختار بعناية لأفضل الاستطلاعات المدفوعة.
                            صنعنا هذا الموقع لأن الوقت ثمين، والحقيقة أثمن.
                        </p>
                        <div className="flex flex-wrap gap-6 text-sm">
                            <Link
                                to="/surveys"
                                className="hover:text-emerald-signal transition-colors"
                                data-testid="footer-surveys"
                            >
                                تصفح الاستطلاعات
                            </Link>
                            <Link
                                to="/about"
                                className="hover:text-emerald-signal transition-colors"
                                data-testid="footer-about"
                            >
                                عن المنصة
                            </Link>
                            <a
                                href="/#flagship"
                                className="hover:text-emerald-signal transition-colors"
                            >
                                العرض المميز
                            </a>
                        </div>
                    </div>
                </div>

                <div className="pt-12 flex flex-wrap items-center justify-between gap-4 text-xs uppercase tracking-widest text-sand-100/60">
                    <div>© 2025 — دليل الاستطلاعات</div>
                    <div className="flex items-center gap-4">
                        <span>الإصدار 01 / صُنع بعناية</span>
                        <button
                            onClick={() => setPinOpen(true)}
                            aria-label="لوحة الإدارة"
                            data-testid="admin-sun"
                            className="text-yellow-400 hover:text-yellow-300 transition-all duration-500 hover:rotate-90"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="4" fill="currentColor" />
                                <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* PIN dialog */}
            <AnimatePresence>
                {pinOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
                            onClick={() => {
                                setPinOpen(false);
                                setPin('');
                            }}
                        />
                        <motion.form
                            initial={{ opacity: 0, y: 20, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.95 }}
                            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                            onSubmit={submitPin}
                            className="fixed z-50 inset-x-4 top-1/3 mx-auto max-w-sm bg-sand-100 text-ink border border-ink p-8"
                            data-testid="sun-pin-dialog"
                        >
                            <div className="flex items-center gap-3 mb-6">
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-yellow-500">
                                    <circle cx="12" cy="12" r="4" fill="currentColor" />
                                    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
                                </svg>
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-ink-400">
                                        وصول مقيّد
                                    </div>
                                    <div className="font-display font-black text-xl">
                                        أدخل الرمز السري
                                    </div>
                                </div>
                            </div>
                            <input
                                type="password"
                                inputMode="numeric"
                                autoFocus
                                value={pin}
                                onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                                maxLength={6}
                                placeholder="•••"
                                className="w-full border border-ink bg-transparent px-4 py-3 text-center text-2xl tracking-[0.5em] font-display font-bold focus:outline-none focus:bg-sand-200"
                                data-testid="sun-pin-input"
                            />
                            <div className="flex gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setPinOpen(false);
                                        setPin('');
                                    }}
                                    className="flex-1 border border-ink py-3 text-sm hover:bg-ink hover:text-sand-100 transition-colors"
                                >
                                    إلغاء
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 bg-ink text-sand-100 py-3 text-sm hover:bg-emerald-signal hover:text-ink transition-colors"
                                    data-testid="sun-pin-submit"
                                >
                                    فتح
                                </button>
                            </div>
                        </motion.form>
                    </>
                )}
            </AnimatePresence>
        </footer>
    );
}
